import { SyntheticEvent } from "react";
import {
  Box,
  Checkbox as MuiCheckbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DynamicQuestionAnswerInput } from "@interfaces/response";
import { DynamicQuestionCatalogItem } from "@interfaces/catalogs";
import CustomRadioGroup from "@components/CustomRadioGroup";
import {
  Checkbox as CheckboxV2,
  ChoiceToggle,
  FieldWrapper,
} from "@components/ui_v2";

// TODO: unificar a un solo diseño (v2) y eliminar la variante "legacy" cuando
// SolicitudCreditoPart2 y demás pantallas antiguas migren a ui_v2.

interface CustomDynamicQuestionFieldProps {
  question: DynamicQuestionCatalogItem;
  value: DynamicQuestionAnswerInput;
  onChange: (value: DynamicQuestionAnswerInput) => void;
  onBlur?: (event: SyntheticEvent) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string | false;
  /**
   * `"legacy"` usa MUI base + CustomRadioGroup (diseño anterior).
   * `"v2"` usa los componentes de ui_v2 (ChoiceToggle, Checkbox, FieldWrapper).
   * Por defecto `"legacy"` para no romper callers existentes.
   */
  variant?: "legacy" | "v2";
}

// Renderiza una pregunta del catálogo real (catalogos/dynamic_question) según su "type":
// single_choice (radio / toggle), multiple_choice (checkboxes) o text (texto libre).
const CustomDynamicQuestionField = ({
  question,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  helperText,
  variant = "legacy",
}: CustomDynamicQuestionFieldProps) => {
  const theme = useTheme();
  const resolvedHelperText = helperText || question.help_text || undefined;
  const options = question.options ?? [];

  // ── single_choice ──────────────────────────────────────────────────────────
  if (question.type === "single_choice") {
    if (variant === "v2") {
      return (
        <FieldWrapper
          label={question.question}
          full
          error={(error && (helperText as string)) || resolvedHelperText || ""}
        >
          <ChoiceToggle
            value={(value as string) ?? ""}
            onChange={(val) => onChange(String(val))}
            options={options.map((o) => ({ label: o.label, value: String(o.value) }))}
            disabled={disabled}
            size="small"
          />
        </FieldWrapper>
      );
    }

    return (
      <CustomRadioGroup
        label={question.question}
        name={`dynamicAnswers.${question.code}`}
        required={question.required}
        value={(value as string) ?? ""}
        onChange={(event) => onChange((event.target as HTMLInputElement).value)}
        onBlur={onBlur}
        disabled={disabled}
        error={error}
        helperText={resolvedHelperText}
        options={options.map((o) => ({ label: o.label, value: String(o.value) }))}
      />
    );
  }

  // ── multiple_choice ────────────────────────────────────────────────────────
  if (question.type === "multiple_choice") {
    const selectedValues = Array.isArray(value) ? value : [];
    const maxReached = Boolean(question.max_selections) && selectedValues.length >= (question.max_selections as number);

    const handleToggle = (optionValue: string, checked: boolean) => {
      const nextValues = checked
        ? [...selectedValues, optionValue]
        : selectedValues.filter((s) => s !== optionValue);
      onChange(nextValues);
    };

    if (variant === "v2") {
      return (
        <FieldWrapper
          label={question.question}
          full
          error={(error && (helperText as string)) || resolvedHelperText || ""}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {options.map((option) => {
              const optionValue = String(option.value);
              const isChecked = selectedValues.includes(optionValue);
              return (
                <CheckboxV2
                  key={optionValue}
                  checked={isChecked}
                  onChange={(checked) => handleToggle(optionValue, checked)}
                  disabled={disabled || (maxReached && !isChecked)}
                  label={
                    <Typography variant="body2" color="primary.dark">
                      {option.label}
                    </Typography>
                  }
                />
              );
            })}
          </Box>
        </FieldWrapper>
      );
    }

    return (
      <Box>
        <Typography color={theme?.palette?.primary?.dark}>
          {question.question}
          {question.required ? " *" : ""}
        </Typography>
        <FormGroup>
          {options.map((option) => {
            const optionValue = String(option.value);
            const isChecked = selectedValues.includes(optionValue);
            return (
              <FormControlLabel
                key={optionValue}
                control={
                  <MuiCheckbox
                    name={`dynamicAnswers.${question.code}`}
                    checked={isChecked}
                    onChange={(event) => handleToggle(optionValue, event.target.checked)}
                    onBlur={onBlur}
                    disabled={disabled || (maxReached && !isChecked)}
                  />
                }
                label={option.label}
              />
            );
          })}
        </FormGroup>
        {resolvedHelperText && (
          <FormHelperText error={error}>{error ? helperText : resolvedHelperText}</FormHelperText>
        )}
      </Box>
    );
  }

  // ── text ───────────────────────────────────────────────────────────────────
  if (variant === "v2") {
    return (
      <FieldWrapper
        label={question.question}
        full
        error={(error && (helperText as string)) || resolvedHelperText || ""}
      >
        <TextField
          autoComplete="off"
          size="lendia_input"
          fullWidth
          name={`dynamicAnswers.${question.code}`}
          value={(value as string) ?? ""}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          error={error}
          inputProps={question.max_length ? { maxLength: question.max_length } : undefined}
        />
      </FieldWrapper>
    );
  }

  return (
    <TextField
      fullWidth
      label={question.question}
      name={`dynamicAnswers.${question.code}`}
      required={question.required}
      value={(value as string) ?? ""}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      error={error}
      helperText={resolvedHelperText}
      margin="normal"
      inputProps={question.max_length ? { maxLength: question.max_length } : undefined}
    />
  );
};

export default CustomDynamicQuestionField;
